import { Fraction } from './fraction'

/**
 * Matrix and vector types using exact Fraction arithmetic
 */
export type FractionMatrix = Fraction[][]
export type FractionVector = Fraction[]

/**
 * Simplex tableau data structure
 */
export interface SimplexTableau {
  tableau: FractionMatrix
  basicVars: number[]
  nonBasicVars: number[]
  objectiveValue: Fraction
  optimalSolution: FractionVector
  isOptimal: boolean
}

/**
 * Linear Programming problem definition
 */
export interface LPProblem {
  A: FractionMatrix  // Constraint matrix
  b: FractionVector  // Right-hand side vector
  c: FractionVector  // Objective function coefficients
}

/**
 * Simplex solver with exact arithmetic
 * Ported from Python reference implementation
 */
export class SimplexSolver {
  
  /**
   * Solve LP problem and return optimal tableau
   * This is the main entry point that mimics the Python getOptimalTableau function
   */
  static getOptimalTableau(A: number[][], b: number[], c: number[]): SimplexTableau {
    // Convert input to exact fractions
    const fractionA = SimplexSolver.numberMatrixToFraction(A)
    const fractionB = SimplexSolver.numberVectorToFraction(b)
    const fractionC = SimplexSolver.numberVectorToFraction(c)
    
    const problem: LPProblem = {
      A: fractionA,
      b: fractionB,
      c: fractionC
    }
    
    return SimplexSolver.solveLP(problem)
  }

  /**
   * Main LP solver using the two-phase simplex method
   */
  private static solveLP(problem: LPProblem): SimplexTableau {
    const { A: _A, b, c: _c } = problem
    
    // Check if all b_i >= 0, if not we need to use two-phase method
    const needTwoPhase = b.some(bi => bi.isNegative())
    
    if (needTwoPhase) {
      return SimplexSolver.twoPhaseMethod(problem)
    } else {
      return SimplexSolver.standardSimplex(problem)
    }
  }

  /**
   * Standard simplex method (when all b_i >= 0)
   */
  private static standardSimplex(problem: LPProblem): SimplexTableau {
    const { A, b, c } = problem
    const m = A.length
    const n = A[0].length
    
    // Add slack variables to convert to standard form
    // Minimize c^T x subject to Ax + s = b, x,s >= 0
    const tableau = SimplexSolver.constructInitialTableau(A, b, c)
    
    // Basic variables are slack variables initially (indices n to n+m-1)
    const basicVars = Array.from({ length: m }, (_, i) => n + i)
    const nonBasicVars = Array.from({ length: n }, (_, i) => i)
    
    return SimplexSolver.solveFromTableau(tableau, basicVars, nonBasicVars)
  }

  /**
   * Two-phase simplex method (when some b_i < 0)
   */
  private static twoPhaseMethod(problem: LPProblem): SimplexTableau {
    const { A, b, c } = problem
    
    // Phase I: Solve auxiliary problem to find initial feasible solution
    const phaseIResult = SimplexSolver.phaseOne(A, b)
    
    if (!phaseIResult.isFeasible) {
      throw new Error('Problem is infeasible')
    }
    
    // Phase II: Solve original problem starting from feasible solution
    return SimplexSolver.phaseTwo(phaseIResult, c, A[0].length, A.length, b)
  }

  /**
   * Phase I of two-phase method
   */
  private static phaseOne(A: FractionMatrix, b: FractionVector): {
    tableau: FractionMatrix
    basicVars: number[]
    nonBasicVars: number[]
    isFeasible: boolean
  } {
    const m = A.length
    const n = A[0].length
    
    // Create auxiliary problem: minimize sum of artificial variables
    // Add slack variables and artificial variables
    const auxTableau: FractionMatrix = []
    
    for (let i = 0; i < m; i++) {
      const row: Fraction[] = []
      
      // Original variables
      for (let j = 0; j < n; j++) {
        row.push(A[i][j])
      }
      
      // Slack variables (one for each constraint)
      for (let j = 0; j < m; j++) {
        row.push(i === j ? Fraction.one() : Fraction.zero())
      }
      
      // Artificial variables (one for each constraint with b_i < 0)
      for (let j = 0; j < m; j++) {
        if (b[j].isNegative()) {
          row.push(i === j ? Fraction.one() : Fraction.zero())
        }
      }
      
      // RHS
      row.push(b[i].abs()) // Use absolute value
      
      auxTableau.push(row)
    }
    
    // Objective row for Phase I: minimize sum of artificial variables
    const objRow: Fraction[] = []
    
    // Coefficients for original variables (0)
    for (let j = 0; j < n; j++) {
      objRow.push(Fraction.zero())
    }
    
    // Coefficients for slack variables (0)
    for (let j = 0; j < m; j++) {
      objRow.push(Fraction.zero())
    }
    
    // Coefficients for artificial variables (1)
    for (let j = 0; j < m; j++) {
      if (b[j].isNegative()) {
        objRow.push(Fraction.one())
      }
    }
    
    // RHS of objective
    objRow.push(Fraction.zero())
    
    auxTableau.push(objRow)
    
    // Set up basic and non-basic variables for auxiliary problem
    // const _artificialCount = b.filter(bi => bi.isNegative()).length
    // const _totalVars = n + m + _artificialCount
    
    const basicVars: number[] = []
    const nonBasicVars: number[] = []
    
    // Non-basic: original variables and some slack variables
    for (let i = 0; i < n + m; i++) {
      nonBasicVars.push(i)
    }
    
    // Basic: artificial variables
    let artificialIndex = n + m
    for (let i = 0; i < m; i++) {
      if (b[i].isNegative()) {
        basicVars.push(artificialIndex++)
      } else {
        basicVars.push(n + i) // slack variable
        // Remove the slack variable from non-basic
        const slackIndex = nonBasicVars.indexOf(n + i)
        if (slackIndex !== -1) {
          nonBasicVars.splice(slackIndex, 1)
        }
      }
    }
    
    // Solve Phase I
    const phaseIResult = SimplexSolver.solveFromTableau(auxTableau, basicVars, nonBasicVars)
    
    // Check if artificial variables are all zero
    const isFeasible = phaseIResult.objectiveValue.isZero()
    
    return {
      tableau: phaseIResult.tableau,
      basicVars: phaseIResult.basicVars,
      nonBasicVars: phaseIResult.nonBasicVars,
      isFeasible
    }
  }

  /**
   * Phase II of two-phase method
   */
  private static phaseTwo(
    phaseIResult: { tableau: FractionMatrix; basicVars: number[]; nonBasicVars: number[]; isFeasible: boolean },
    c: FractionVector,
    n: number,
    m: number,
    b: FractionVector
  ): SimplexTableau {
    // Copy tableau
    let tableau = SimplexSolver.deepCopyMatrix(phaseIResult.tableau)
    let basicVars = [...phaseIResult.basicVars]
    let nonBasicVars = [...phaseIResult.nonBasicVars]

    // Determine artificial columns range: appear after original(n) + slack(m)
    const artificialStart = n + m
    const numArtificial = b.filter((bi) => bi.isNegative()).length
    const artificialEnd = artificialStart + numArtificial - 1

    // Pivot out any artificial basic variables
    for (let i = 0; i < m; i++) {
      const bv = basicVars[i]
      if (bv >= artificialStart && bv <= artificialEnd) {
        // Find a non-artificial column with non-zero entry to pivot in
        let enter = -1
        for (let j = 0; j < artificialStart; j++) {
          if (!tableau[i][j].isZero()) { enter = j; break }
        }
        if (enter === -1) {
          // If no non-artificial column available, this row may be redundant; skip
          continue
        }
        // Perform pivot
        SimplexSolver.pivot(tableau, i, enter)
        // Update basis lists
        const leaving = basicVars[i]
        basicVars[i] = enter
        const nbIdx = nonBasicVars.indexOf(enter)
        if (nbIdx !== -1) nonBasicVars[nbIdx] = leaving
      }
    }

    // Remove artificial columns from tableau
    const keepCols = new Set<number>()
    for (let j = 0; j < artificialStart; j++) keepCols.add(j)
    // RHS column index (last)
    const rhsIndex = tableau[0].length - 1
    keepCols.add(rhsIndex)

    tableau = tableau.map((row) => row.filter((_, idx) => keepCols.has(idx)))

    // Recompute nonBasic as complement of basic among 0..(n+m-1)
    nonBasicVars = []
    for (let j = 0; j < n + m; j++) {
      if (!basicVars.includes(j)) nonBasicVars.push(j)
    }

    // Restore objective row for original problem: -c for first n, 0 for slacks, RHS 0
    const newObjRow: Fraction[] = []
    for (let j = 0; j < n; j++) newObjRow.push(c[j].negate())
    for (let j = 0; j < m; j++) newObjRow.push(Fraction.zero())
    newObjRow.push(Fraction.zero()) // RHS
    tableau[tableau.length - 1] = newObjRow

    // Make objective row consistent with current basis (reduced costs)
    const objRow = tableau[tableau.length - 1]
    for (let i = 0; i < m; i++) {
      const bv = basicVars[i]
      const coeff = objRow[bv]
      if (!coeff.isZero()) {
        for (let j = 0; j < tableau[i].length; j++) {
          objRow[j] = objRow[j].subtract(coeff.multiply(tableau[i][j]))
        }
      }
    }

    // Solve Phase II from this feasible basis
    return SimplexSolver.solveFromTableau(tableau, basicVars, nonBasicVars)
  }

  /**
   * Construct initial simplex tableau
   */
  private static constructInitialTableau(A: FractionMatrix, b: FractionVector, c: FractionVector): FractionMatrix {
    const m = A.length
    const n = A[0].length
    
    const tableau: FractionMatrix = []
    
    // Constraint rows
    for (let i = 0; i < m; i++) {
      const row: Fraction[] = []
      
      // A matrix entries
      for (let j = 0; j < n; j++) {
        row.push(A[i][j])
      }
      
      // Slack variables (identity matrix)
      for (let j = 0; j < m; j++) {
        row.push(i === j ? Fraction.one() : Fraction.zero())
      }
      
      // RHS
      row.push(b[i])
      
      tableau.push(row)
    }
    
    // Objective row (for minimization in canonical form)
    const objRow: Fraction[] = []
    
    // Original variables (negate for minimization in standard tableau form)
    for (let j = 0; j < n; j++) {
      objRow.push(c[j].negate()) // Negate for minimization
    }
    
    // Slack variables
    for (let j = 0; j < m; j++) {
      objRow.push(Fraction.zero())
    }
    
    // RHS of objective
    objRow.push(Fraction.zero())
    
    tableau.push(objRow)
    
    return tableau
  }

  /**
   * Solve from given tableau using simplex iterations
   */
  private static solveFromTableau(tableau: FractionMatrix, basicVars: number[], nonBasicVars: number[]): SimplexTableau {
    let currentTableau = SimplexSolver.deepCopyMatrix(tableau)
    let currentBasic = [...basicVars]
    let currentNonBasic = [...nonBasicVars]
    
    const maxIterations = 1000
    let iterations = 0
    
    while (iterations < maxIterations) {
      // Check optimality
      if (SimplexSolver.isOptimal(currentTableau)) {
        break
      }
      
      // Find entering variable (most negative in objective row for minimization)
      const enteringVar = SimplexSolver.findEnteringVariable(currentTableau, currentNonBasic)
      if (enteringVar === -1) {
        break // Optimal solution found
      }
      
      // Find leaving variable using ratio test
      const leavingVarIndex = SimplexSolver.findLeavingVariable(currentTableau, enteringVar)
      if (leavingVarIndex === -1) {
        throw new Error('Problem is unbounded')
      }
      
      // Perform pivot operation
      SimplexSolver.pivot(currentTableau, leavingVarIndex, enteringVar)
      
      // Update basic and non-basic variable lists
      const leavingVar = currentBasic[leavingVarIndex]
      currentBasic[leavingVarIndex] = enteringVar
      
      const enteringVarIndex = currentNonBasic.indexOf(enteringVar)
      currentNonBasic[enteringVarIndex] = leavingVar
      
      iterations++
    }
    
    if (iterations >= maxIterations) {
      throw new Error('Simplex method did not converge within maximum iterations')
    }
    
    // Extract solution
    const solution = SimplexSolver.extractSolution(currentTableau, currentBasic, nonBasicVars.length + basicVars.length)
    const objectiveValue = currentTableau[currentTableau.length - 1][currentTableau[0].length - 1]
    
    return {
      tableau: currentTableau,
      basicVars: currentBasic,
      nonBasicVars: currentNonBasic,
      objectiveValue,
      optimalSolution: solution,
      isOptimal: true
    }
  }

  /**
   * Check if current tableau represents optimal solution
   */
  private static isOptimal(tableau: FractionMatrix): boolean {
    const objRow = tableau[tableau.length - 1]
    
    // For minimization: optimal if all coefficients (except RHS) are non-positive
    // (since we're looking for negative coefficients to enter)
    for (let j = 0; j < objRow.length - 1; j++) {
      if (objRow[j].isNegative()) {
        return false
      }
    }
    
    return true
  }

  /**
   * Find entering variable (most negative coefficient in objective row)
   */
  private static findEnteringVariable(tableau: FractionMatrix, _nonBasicVars: number[]): number {
    const objRow = tableau[tableau.length - 1]
    for (let j = 0; j < objRow.length - 1; j++) {
      if (objRow[j].isNegative()) {
        return j
      }
    }
    
    return -1
  }

  /**
   * Find leaving variable using minimum ratio test
   */
  private static findLeavingVariable(tableau: FractionMatrix, enteringVar: number): number {
    const m = tableau.length - 1 // Exclude objective row
    let minRatio = new Fraction(BigInt(Number.MAX_SAFE_INTEGER))
    let leavingVarIndex = -1
    
    for (let i = 0; i < m; i++) {
      const pivot = tableau[i][enteringVar]
      const rhs = tableau[i][tableau[i].length - 1]
      
      if (pivot.isPositive()) {
        const ratio = rhs.divide(pivot)
        if (ratio.lessThan(minRatio) || (ratio.equals(minRatio) && (leavingVarIndex === -1 || i < leavingVarIndex))) {
          minRatio = ratio
          leavingVarIndex = i
        }
      }
    }
    
    return leavingVarIndex
  }

  /**
   * Perform pivot operation
   */
  private static pivot(tableau: FractionMatrix, pivotRow: number, pivotCol: number): void {
    const pivotElement = tableau[pivotRow][pivotCol]
    
    // Scale pivot row
    for (let j = 0; j < tableau[pivotRow].length; j++) {
      tableau[pivotRow][j] = tableau[pivotRow][j].divide(pivotElement)
    }
    
    // Eliminate other entries in pivot column
    for (let i = 0; i < tableau.length; i++) {
      if (i !== pivotRow) {
        const multiplier = tableau[i][pivotCol]
        for (let j = 0; j < tableau[i].length; j++) {
          tableau[i][j] = tableau[i][j].subtract(multiplier.multiply(tableau[pivotRow][j]))
        }
      }
    }
  }

  /**
   * Extract optimal solution from tableau
   */
  private static extractSolution(tableau: FractionMatrix, basicVars: number[], totalVars: number): FractionVector {
    const solution: Fraction[] = new Array(totalVars).fill(Fraction.zero())
    
    for (let i = 0; i < basicVars.length; i++) {
      const varIndex = basicVars[i]
      if (varIndex < totalVars) {
        solution[varIndex] = tableau[i][tableau[i].length - 1]
      }
    }
    
    return solution
  }

  /**
   * Deep copy matrix
   */
  private static deepCopyMatrix(matrix: FractionMatrix): FractionMatrix {
    return matrix.map(row => [...row])
  }

  /**
   * Convert number matrix to fraction matrix
   */
  private static numberMatrixToFraction(matrix: number[][]): FractionMatrix {
    return matrix.map(row => row.map(val => Fraction.fromDecimal(val)))
  }

  /**
   * Convert number vector to fraction vector
   */
  private static numberVectorToFraction(vector: number[]): FractionVector {
    return vector.map(val => Fraction.fromDecimal(val))
  }

  /**
   * Utility function to print tableau (for debugging)
   */
  static printTableau(tableau: SimplexTableau): void {
    console.log('Simplex Tableau:')
    console.log('Basic Variables:', tableau.basicVars)
    console.log('Non-Basic Variables:', tableau.nonBasicVars)
    console.log('Tableau:')
    
    tableau.tableau.forEach((row, i) => {
      const rowStr = row.map(val => val.toString().padStart(8)).join(' ')
      console.log(`Row ${i}: ${rowStr}`)
    })
    
    console.log('Objective Value:', tableau.objectiveValue.toString())
    console.log('Optimal Solution:', tableau.optimalSolution.map(x => x.toString()))
  }
}
